<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import type { RequestScenario } from '@/types/admin'
import { PAGE_DEFINITIONS } from '@/data/registry'
import { useAuthStore } from '@/stores/auth'
import { usePrototypeStore } from '@/stores/prototype'
import { useTaskStore } from '@/stores/tasks'
import { useUiStore } from '@/stores/ui'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { simulatedFault, SCENARIO_LABELS, resetFaults } from '@/services/faults'
import { formatDateTime, formatNumber } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'

const auth = useAuthStore()
const store = usePrototypeStore()
const tasks = useTaskStore()
const ui = useUiStore()
const router = useRouter()

const canMaintain = computed(() => auth.has('ops.maintain'))
const selectedGroup = ref('全部')
const resetBusy = ref(false)
const maintenanceBusy = ref(false)
const taskBusy = ref('')
const now = ref(Date.now())
const asyncTasks = computed(() => {
  void now.value
  return adapter.table('async_tasks').slice(0, 8)
})
const runtimeTasks = computed(() => tasks.recent.slice(0, 6))
const maintenance = ref(adapter.settings.maintenanceEnabled)
const stateScenarios = [
  { code: '403', label: '无权访问', route: '/403', tone: 'warning' },
  { code: '404', label: '页面不存在', route: '/404', tone: 'info' },
  { code: '500', label: '请求失败', route: '/500', tone: 'danger' },
  { code: 'M', label: '维护窗口', route: '/maintenance', tone: 'info' },
  { code: '—', label: '网络断开', route: '/offline', tone: 'warning' },
]

const groups = ['全部', '仪表盘', '用户管理', '家庭档案', '用药管理', '健康监测', '药品库', '消息通知', '运营中心', '客服与反馈', '数据分析', '系统设置', '基础页面']
const filteredPages = computed(() => selectedGroup.value === '全部' ? PAGE_DEFINITIONS : PAGE_DEFINITIONS.filter(page => page.group === selectedGroup.value))
const tablePages = computed(() => PAGE_DEFINITIONS.filter(page => ['table', 'tree-table', 'dashboard-table', 'grid'].includes(page.kind)))
const readOnlyPages = computed(() => PAGE_DEFINITIONS.filter(page => page.readOnly))
const specialPages = computed(() => PAGE_DEFINITIONS.filter(page => ['dashboard', 'editor', 'form', 'state'].includes(page.kind)))
const totalPermissions = computed(() => new Set(PAGE_DEFINITIONS.flatMap(page => [page.permission, ...Object.values(page.permissions)])).size)
const openAlerts = computed(() => store.db.alerts.filter(alert => ['pending', 'processing'].includes(String(alert.status))).length)
const onlineDevices = computed(() => store.db.devices.filter(device => device.status === 'online').length)
const auditCount = computed(() => store.db.audit_logs.length)

const scenarios = (Object.keys(SCENARIO_LABELS) as RequestScenario[]).map(key => ({ key, label: SCENARIO_LABELS[key] }))

const TASK_STATUS: Record<string, { label: string; tone: string }> = {
  queued: { label: '排队中', tone: 'info' },
  running: { label: '进行中', tone: 'info' },
  success: { label: '已完成', tone: 'success' },
  partial_success: { label: '部分失败', tone: 'warning' },
  failed: { label: '已失败', tone: 'danger' },
  cancelled: { label: '已取消', tone: 'neutral' },
  '排队中': { label: '排队中', tone: 'info' },
  '进行中': { label: '进行中', tone: 'info' },
  '已完成': { label: '已完成', tone: 'success' },
  '部分失败': { label: '部分失败', tone: 'warning' },
  '已失败': { label: '已失败', tone: 'danger' },
  '已取消': { label: '已取消', tone: 'neutral' },
}

function taskStatusLabel(value: unknown): string {
  return TASK_STATUS[String(value)]?.label ?? '未知状态'
}

function taskStatusTone(value: unknown): string {
  return TASK_STATUS[String(value)]?.tone ?? 'neutral'
}

const activeScenario = computed(() => simulatedFault.offline ? 'offline' : simulatedFault.scenario)

function setScenario(scenario: RequestScenario): void {
  if (!canMaintain.value) {
    ui.toast('warning', '没有运维维护权限', '当前角色只能查看运行状态。')
    return
  }
  if (scenario === 'offline') {
    ui.setOffline(true)
    simulatedFault.scenario = 'normal'
  } else {
    ui.setOffline(false)
    simulatedFault.scenario = scenario
  }
  now.value = Date.now()
  ui.toast(scenario === 'normal' ? 'success' : 'info', `已切换为${SCENARIO_LABELS[scenario]}`, '下一次列表或写操作请求会演示对应状态。')
}

function reset(): void {
  resetFaults()
  ui.setOnline(typeof navigator === 'undefined' ? true : navigator.onLine)
  now.value = Date.now()
  ui.toast('success', '故障注入已复位', '请求层恢复为正常状态。')
}

function setLatency(value: number): void {
  simulatedFault.latency = Math.max(0, Math.min(3000, value))
}

async function resetData(): Promise<void> {
  if (!canMaintain.value) return
  const allowed = await ui.confirm({
    title: '重置原型内存',
    message: '将清空当前浏览器内存中的新增、编辑、状态变化与运维任务，恢复可复现种子数据。',
    confirmText: '重置数据',
    tone: 'danger',
    detail: '不会写入任何持久化存储，也不会影响正式服务。',
  })
  if (!allowed) return
  resetBusy.value = true
  store.reset()
  resetFaults()
  ui.setOnline(typeof navigator === 'undefined' ? true : navigator.onLine)
  tasks.tasks.splice(0)
  tasks.addSeedTasks(store.db.async_tasks as Array<Record<string, unknown>>)
  resetBusy.value = false
  ui.toast('success', '原型数据已恢复', '当前页面列表会在下一次刷新后展示种子数据。')
  void router.replace('/dashboard')
}

async function toggleMaintenance(): Promise<void> {
  if (!canMaintain.value || maintenanceBusy.value) return
  const nextValue = !maintenance.value
  if (nextValue) {
    const allowed = await ui.confirm({
      title: '开启维护模式',
      message: '启用后业务页面会跳转到维护页，已登录会话和筛选上下文保留。',
      confirmText: '开启维护',
      tone: 'danger',
      detail: '这是高危运维操作，会写入当前原型的操作日志。',
    })
    if (!allowed) return
  }
  maintenanceBusy.value = true
  try {
    const next = await request(
      () => adapter.updateSettings({ maintenanceEnabled: nextValue }, auth.profile?.name ?? '管理员'),
      { write: true, delay: 320, useSimulation: true },
    )
    maintenance.value = next.maintenanceEnabled
    now.value = Date.now()
    ui.toast(maintenance.value ? 'warning' : 'success', maintenance.value ? '维护模式已开启' : '维护模式已关闭', '可在系统参数页维护说明与预计恢复时间。')
  } catch (error) {
    ui.toast('error', '维护模式切换失败', error instanceof Error ? error.message : '请稍后重试')
  } finally {
    maintenanceBusy.value = false
  }
}

function refreshStatus(): void {
  now.value = Date.now()
  ui.toast('success', '运行状态已刷新', '当前页面重新读取内存任务、路由与权限事实。')
}

async function retryFailedTask(id: string): Promise<void> {
  if (!canMaintain.value || taskBusy.value) return
  taskBusy.value = id
  try {
    await request(
      () => adapter.retryTask(id),
      { write: true, delay: 280, useSimulation: true },
    )
    now.value = Date.now()
    ui.toast('success', '失败任务已重新进入队列', '可在任务中心查看进度和结果。')
  } catch (error) {
    ui.toast('error', '任务重试失败', error instanceof Error ? error.message : '请稍后重试')
  } finally {
    taskBusy.value = ''
  }
}

function showTask(row: Record<string, unknown>): void {
  const lines = [
    `任务编号：${String(row.task_no ?? row.taskNo ?? row.id ?? '—')}`,
    `任务类型：${String(row.task_type ?? row.taskType ?? '—')}`,
    `当前状态：${taskStatusLabel(row.status)}`,
    `进度：${String(row.progress ?? 0)}%`,
    `创建时间：${formatDateTime(row.created_at ?? row.createdAt)}`,
    `最近信息：${String(row.message ?? '—')}`,
  ]
  void ElMessageBox.alert(lines.join('\n'), '任务事件详情', {
    confirmButtonText: '知道了',
    customStyle: { whiteSpace: 'pre-line' },
  }).catch(() => undefined)
}

function go(route: string): void {
  void router.push(route)
}

</script>

<template>
  <section class="operations-page">
    <header class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">运维管理</h1>
          <span class="prototype-pill">原型诊断</span>
          <span class="tag" :class="maintenance ? 'tag--warning' : 'tag--success'">{{ maintenance ? '维护窗口' : '运行中' }}</span>
        </div>
        <p class="page-subtitle">{{ (PAGE_DEFINITIONS.find(page => page.id === 'operations')?.purpose) }}</p>
      </div>
      <div class="page-header__actions">
        <button type="button" class="btn" @click="refreshStatus"><AppIcon name="refresh" :size="14" />刷新状态</button>
        <button type="button" class="btn" :disabled="!canMaintain || maintenanceBusy" @click="toggleMaintenance">
          <span v-if="maintenanceBusy" class="button-spinner" aria-hidden="true" />{{ maintenance ? '关闭维护模式' : '开启维护模式' }}
        </button>
        <button type="button" class="btn" :disabled="!canMaintain || resetBusy" @click="resetData"><AppIcon name="refresh" :size="14" />重置原型数据</button>
        <button type="button" class="btn btn--primary" @click="reset">复位故障注入</button>
      </div>
    </header>

    <div class="ops-grid">
      <article class="ops-card"><div class="ops-card__label">已注册路由</div><div class="ops-card__value">{{ formatNumber(PAGE_DEFINITIONS.length) }}</div><div class="ops-card__foot">全部由同一路由表驱动菜单、面包屑与标签</div></article>
      <article class="ops-card"><div class="ops-card__label">列表 / 业务页</div><div class="ops-card__value">{{ formatNumber(tablePages.length + specialPages.length) }}</div><div class="ops-card__foot">语义筛选、分页、详情与表单已按页面声明生成</div></article>
      <article class="ops-card"><div class="ops-card__label">权限点</div><div class="ops-card__value">{{ formatNumber(totalPermissions) }}</div><div class="ops-card__foot">菜单、路由、指令与服务请求共同校验</div></article>
      <article class="ops-card"><div class="ops-card__label">待处理预警</div><div class="ops-card__value">{{ formatNumber(openAlerts) }}</div><div class="ops-card__foot">SLA 规则在预警列表与工作台同步</div></article>
      <article class="ops-card"><div class="ops-card__label">在线设备</div><div class="ops-card__value">{{ formatNumber(onlineDevices) }}</div><div class="ops-card__foot">设备命令与批量同步进入任务中心</div></article>
      <article class="ops-card"><div class="ops-card__label">审计日志</div><div class="ops-card__value">{{ formatNumber(auditCount) }}</div><div class="ops-card__foot">只读追加，不提供编辑与删除</div></article>
    </div>

    <div class="dash-grid dash-grid--second" style="margin-top: 14px">
      <article class="card">
        <div class="card__head">
          <div><h2 class="card__title">交互状态与故障注入</h2><p class="card__sub">只影响当前浏览器的原型请求，不改变正式服务</p></div>
          <span class="tag" :class="activeScenario === 'normal' ? 'tag--success' : 'tag--warning'">{{ SCENARIO_LABELS[activeScenario] }}</span>
        </div>
        <div class="fault-panel" style="padding: 0 16px 16px">
          <div class="fault-panel__grid">
            <button v-for="scenario in scenarios" :key="scenario.key" type="button" class="fault-panel__scenario" :class="{ 'is-active': activeScenario === scenario.key }" :disabled="!canMaintain" @click="setScenario(scenario.key)">
              <span>{{ scenario.label }}</span>
              <small>{{ scenario.key }}</small>
            </button>
          </div>
          <div class="fault-panel__row">
            <span>请求延迟</span>
            <el-slider :model-value="simulatedFault.latency" :min="0" :max="2000" :step="20" style="width: 220px" :disabled="!canMaintain" @update:model-value="setLatency(Number($event))" />
            <b>{{ simulatedFault.latency }} ms</b>
          </div>
          <div class="fault-panel__note">
            超时、401、403、404、409、422、429、500 与断网会在下一次请求时触发；401 会清理会话并回到登录页，写操作失败时界面保留原值并显示明确错误。
          </div>
        </div>
      </article>

      <article class="card">
        <div class="card__head">
          <div><h2 class="card__title">状态页与恢复入口</h2><p class="card__sub">只验证当前 Vue 原型内的页面状态，不代表任何正式服务</p></div>
          <button type="button" class="btn btn--sm" @click="go('/settings/general')">系统参数</button>
        </div>
        <div class="ops-state-grid">
          <button v-for="state in stateScenarios" :key="state.code" type="button" class="ops-state" @click="go(state.route)">
            <span class="ops-state__code" :class="`ops-state__code--${state.tone}`">{{ state.code }}</span>
            <span><b>{{ state.label }}</b><small>{{ state.route }}</small></span>
            <AppIcon name="arrow-right" :size="13" />
          </button>
        </div>
        <div class="ops-runtime">
          <div><span>当前视口</span><b>{{ ui.viewportWidth }} px</b></div>
          <div><span>侧栏形态</span><b>{{ ui.viewportWidth >= 1100 ? '固定侧栏' : '抽屉侧栏' }}</b></div>
          <div><span>当前数据范围</span><b>{{ ui.dataScopeId ? '区域筛选' : '全部区域' }}</b></div>
          <div><span>任务中心</span><b>{{ tasks.recent.length }} 条会话任务</b></div>
        </div>
      </article>
    </div>

    <section class="card" style="margin-top: 14px">
      <div class="card__head">
        <div><h2 class="card__title">路由验收矩阵</h2><p class="card__sub">每一行都是可打开的 Vue 组件路由</p></div>
        <div class="segmented">
          <button v-for="group in groups" :key="group" type="button" class="segmented__item" :class="{ 'segmented__item--active': selectedGroup === group }" @click="selectedGroup = group">{{ group }}</button>
        </div>
      </div>
      <div class="route-matrix" style="padding: 0 16px 16px">
        <button v-for="page in filteredPages" :key="page.id" type="button" class="route-matrix__item" @click="go(page.route)">
          <AppIcon :name="page.icon" :size="13" />
          <b>{{ page.title }}</b>
          <code>{{ page.route.replace(/^#/, '') }}</code>
        </button>
      </div>
    </section>

    <div class="dash-grid dash-grid--second" style="margin-top: 14px">
      <article class="card">
        <div class="card__head"><div><h2 class="card__title">异步任务</h2><p class="card__sub">导入、导出、批量与设备任务</p></div><button type="button" class="btn btn--sm" @click="ui.tasksOpen = true">任务中心</button></div>
        <div class="table-scroll">
          <table class="pro-table">
            <thead><tr><th>任务</th><th>状态</th><th>进度</th><th>创建时间</th><th>操作</th></tr></thead>
            <tbody>
              <tr v-for="task in asyncTasks" :key="String(task.id)">
                <td>{{ task.title }}</td>
                <td><span class="tag" :class="`tag--${taskStatusTone(task.status)}`">{{ taskStatusLabel(task.status) }}</span></td>
                <td class="is-num">{{ task.progress ?? 0 }}%</td>
                <td>{{ formatDateTime(task.created_at) }}</td>
                <td>
                  <div class="row-actions">
                    <button type="button" class="row-action" @click="showTask(task)">事件详情</button>
                    <template v-if="['failed', 'partial_success', '部分失败', '已失败'].includes(String(task.status))">
                      <span class="row-action-sep" aria-hidden="true" />
                      <button type="button" class="row-action row-action--danger" :disabled="!canMaintain || taskBusy === String(task.id)" @click="retryFailedTask(String(task.id))">
                        {{ taskBusy === String(task.id) ? '重试中…' : '处理失败任务' }}
                      </button>
                    </template>
                  </div>
                </td>
              </tr>
              <tr v-if="!asyncTasks.length"><td colspan="5" class="cell-muted">暂无任务</td></tr>
            </tbody>
          </table>
        </div>
      </article>
      <article class="card">
        <div class="card__head"><div><h2 class="card__title">最近运行任务</h2><p class="card__sub">当前页面创建的任务会即时进入任务中心</p></div></div>
        <div class="table-scroll">
          <table class="pro-table">
            <thead><tr><th>任务</th><th>状态</th><th>进度</th><th>创建时间</th><th>操作</th></tr></thead>
            <tbody>
              <tr v-for="task in runtimeTasks" :key="task.id">
                <td>{{ task.title }}</td>
                <td><span class="tag" :class="`tag--${taskStatusTone(task.status)}`">{{ taskStatusLabel(task.status) }}</span></td>
                <td class="is-num">{{ task.progress }}%</td>
                <td>{{ formatDateTime(task.createdAt) }}</td>
                <td><button type="button" class="row-action" @click="showTask(task as unknown as Record<string, unknown>)">事件详情</button></td>
              </tr>
              <tr v-if="!runtimeTasks.length"><td colspan="5" class="cell-muted">当前会话尚未创建任务</td></tr>
            </tbody>
          </table>
        </div>
      </article>
    </div>

    <p class="faint" style="margin-top: 10px">本页只展示当前 Vue 原型的内存运行事实：路由、权限点、任务进度与故障注入状态；不连接正式认证、业务接口或数据服务。</p>
  </section>
</template>


<style scoped>
.ops-state-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; padding: 0 16px 12px; }
.ops-state { display: flex; align-items: center; gap: 8px; min-height: 46px; padding: 8px 9px; border: 1px solid var(--c-border); border-radius: 9px; background: #fff; text-align: left; }
.ops-state:hover { border-color: var(--c-success-border); background: var(--c-brand-mist); }
.ops-state__code { flex: 0 0 auto; width: 34px; height: 28px; display: grid; place-items: center; border-radius: 7px; background: var(--c-surface-muted); color: var(--c-muted); font-family: var(--font-num); font-size: 10px; font-weight: 700; }
.ops-state__code--danger { background: var(--c-danger-soft); color: var(--c-danger); }
.ops-state__code--warning { background: var(--c-warning-soft); color: #93600b; }
.ops-state__code--info { background: var(--c-info-soft); color: var(--c-info); }
.ops-state > span:nth-child(2) { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ops-state b { color: var(--c-text); font-size: 10.5px; }
.ops-state small { margin-top: 1px; color: var(--c-faint); font-size: 9px; }
.ops-runtime { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; margin: 0 16px 16px; padding-top: 11px; border-top: 1px solid var(--c-divider); }
.ops-runtime div { padding: 8px 9px; border-radius: 8px; background: var(--c-surface-muted); }
.ops-runtime span { display: block; color: var(--c-muted); font-size: 9.5px; }
.ops-runtime b { display: block; margin-top: 3px; color: var(--c-ink); font-size: 11px; font-weight: 600; }
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
@media (max-width: 767px) { .ops-state-grid, .ops-runtime { grid-template-columns: minmax(0, 1fr); } }
</style>
